import { Component, computed, OnInit, Signal, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Address } from '../../core/models/address.model';
import { AddressService } from '../../core/services/address.service';
import { ClockService } from '../../core/services/clock.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { OutcomeChipComponent } from '../../shared/components/outcome-chip/outcome-chip.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { RelativeTimePipe } from '../../shared/pipes/relative-time.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/**
 * Address list. `addresses` is a reactive query over the whole table; the
 * search box filters it client-side so both the filter and incoming sync
 * changes apply instantly without re-querying.
 */
@Component({
  selector: 'app-addresses',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    FormlyModule,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    SpinnerComponent,
    OutcomeChipComponent,
    HasPermissionDirective,
    RelativeTimePipe,
    TranslatePipe,
  ],
  templateUrl: './addresses.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './addresses.component.css',
})
export class AddressesComponent implements OnInit {
  private addressService = inject(AddressService);
  private translate = inject(TranslationService);

  readonly now = inject(ClockService).now;

  addresses!: Signal<Address[]>;
  loading!: Signal<boolean>;
  error!: Signal<string | null>;

  searchTerm = signal('');
  searchForm = new FormGroup({});
  searchModel = { term: '' };
  searchFields: FormlyFieldConfig[] = [];

  filtered = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const all = this.addresses();
    if (!term) return all;
    return all.filter(
      (a) =>
        a.formatted_address.toLowerCase().includes(term) ||
        a.business_name.toLowerCase().includes(term),
    );
  });

  async ngOnInit(): Promise<void> {
    const query = this.addressService.createQuery();
    this.addresses = query.data;
    this.loading = query.loading;
    this.error = query.error;

    this.searchFields = [
      {
        key: 'term',
        type: 'text',
        props: {
          placeholder: this.translate.translate('addresses.list.searchPlaceholder'),
          attributes: { 'data-testid': 'address-search-input', autocomplete: 'off' },
        },
      },
    ];
    this.searchForm.valueChanges.subscribe((v: { term?: string }) => this.searchTerm.set(v.term ?? ''));
  }
}
